import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Doc } from '../lib/api';
import { R, btnAccent, btnOutline, field, fieldLabelBlock } from '../lib/ui';
import { Modal } from './Modal';
import { IconPicker } from './IconPicker';
import { Select } from './Select';

export function DocPropertiesModal({ doc, onClose }: { doc: Doc; onClose: () => void }) {
  const qc = useQueryClient();

  const [name, setName] = useState(doc.name);
  const [icon, setIcon] = useState(doc.icon ?? 'file');
  const [sectionId, setSectionId] = useState(doc.sectionId);

  const sections = useQuery({ queryKey: ['sections'], queryFn: api.sections });

  const save = useMutation({
    mutationFn: () =>
      api.updateDoc(doc.id, { name: name.trim() || doc.name, icon, sectionId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['docs'] });
      qc.invalidateQueries({ queryKey: ['sections'] });
      qc.invalidateQueries({ queryKey: ['doc', doc.id] });
      onClose();
    },
  });

  return (
    <Modal title="Свойства документа" onClose={onClose} width={440}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <label style={{ ...fieldLabelBlock, marginTop: 0 }}>Название</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <IconPicker value={icon} onChange={setIcon} size={40} />
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ ...field, flex: 1 }}
          />
        </div>

        <label style={fieldLabelBlock}>Раздел</label>
        <Select
          value={sectionId}
          onChange={setSectionId}
          options={(sections.data ?? []).map((s) => ({
            value: s.id,
            label: s.name,
            hint: String(s.docCount),
          }))}
        />

        {save.isError && (
          <div style={{ marginTop: 12, fontSize: 12, color: R.danger }}>
            {(save.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={onClose} style={btnOutline}>
            Отмена
          </button>
          <button
            type="submit"
            disabled={!name.trim() || save.isPending}
            style={{ ...btnAccent, opacity: !name.trim() || save.isPending ? 0.5 : 1 }}
          >
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
